import { MenuItem } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useIsInsidePwa } from "../useIsInsidePwa";
import { usePwaStore } from "../usePwaStore";
import { isDesktop } from "react-device-detect";
import { QrCodeModal } from "../qr-code-modal/QrCodeModal";
import { useState } from "react";
import {
    ManualInstallationInstructionsDrawer,
    supportsManualInstallation,
} from "../instructions/ManualInstallationInstructionsDrawer";

interface MobileAppMenuItemProps {
    appName: string;
    appIconUrl: string;
    closeMenu: () => void;
}

/**
 * Button linking to the mobile app.
 */
export const MobileAppMenuItem = ({ closeMenu, appName, appIconUrl, ...rest }: MobileAppMenuItemProps) => {
    const isInsidePwa = useIsInsidePwa();
    const { canInstall, install, dismissed } = usePwaStore();

    const [showQrCode, setShowQrCode] = useState(false);
    const [showManualInstallationInstructions, setShowManualInstallationInstructions] = useState(false);

    if (isInsidePwa) {
        return null;
    }

    let handleClick: () => void | Promise<void>;
    if (isDesktop) {
        // On desktop, the "mobile app" button is always present, and leads to the QR code
        handleClick = () => setShowQrCode(true);
    }
    else {
        // On mobile, the "mobile app" button shows only after dismissing the main banner
        if (!dismissed) {
            return null;
        }

        if (supportsManualInstallation) {
            // On iOS, we can't detect whether the app was installed or not, so we always show the installation instructions.
            handleClick = () => setShowManualInstallationInstructions(true);
        }
        else {
            // On android, the "mobile app" button shows only when the app can actually be installed.
            if (!canInstall) {
                return null;
            }

            handleClick = async () => {
                closeMenu();
                install();
            };
        }
    }

    return (
        <>
            <MenuItem {...rest} onClick={handleClick}>
                {translate("The mobile app")}
            </MenuItem>

            <QrCodeModal
                open={showQrCode}
                onClose={() => {
                    setShowQrCode(false);
                    closeMenu();
                }}
            />

            <ManualInstallationInstructionsDrawer
                open={showManualInstallationInstructions}
                onClose={() => {
                    setShowManualInstallationInstructions(false);
                    closeMenu();
                }}
                appName={appName}
                appIconUrl={appIconUrl}
                isDesktop={isDesktop}
            />
        </>
    );
};
